import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { FaChartLine, FaChartBar, FaChartPie, FaArrowLeft } from 'react-icons/fa';
import SimpleChart from '../../components/SimpleChart';
import PieChart from '../../components/PieChart';
import LineChart from '../../components/LineChart';

const AdminAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [chartType, setChartType] = useState('bar'); // Single chart type for all charts
  const { user } = useAuth();

  useEffect(() => {
    fetchDashboardData();
    fetchAnalytics();
  }, [period]);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/api/admin/dashboard');
      setStats(response.data);
    } catch (error) {
      console.error('Fetch dashboard error:', error);
      toast.error('Failed to load dashboard data');
    }
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/analytics', { params: { period } });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Fetch analytics error:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };


  // Prepare data for pie charts
  const userRoleData = stats?.stats ? [
    { label: 'Riders', value: stats.stats.totalRiders },
    { label: 'Passengers', value: stats.stats.totalPassengers }
  ].filter(item => item.value > 0) : [];

  const tripStatusData = stats?.stats ? [
    { label: 'Completed', value: stats.stats.completedTrips },
    { label: 'Active', value: stats.stats.activeTrips },
    { label: 'Open', value: (stats.stats.totalTrips - stats.stats.completedTrips - stats.stats.activeTrips) }
  ].filter(item => item.value > 0) : [];

  const bookingStatusData = stats?.stats ? [
    { label: 'Completed', value: stats.stats.completedBookings },
    { label: 'Total', value: stats.stats.totalBookings }
  ].filter(item => item.value > 0) : [];

  if (loading && !analytics) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      <div className="container mx-auto px-4 py-4 sm:py-6 lg:py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-4 sm:mb-6 lg:mb-8">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
              <div>
                <a href="/admin" className="text-blue-600 hover:text-blue-700 mb-2 inline-flex items-center text-sm">
                  <FaArrowLeft className="mr-2" />
                  Back to Dashboard
                </a>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Analytics & Reports</h1>
                <p className="text-sm sm:text-base text-gray-600">Detailed analytics and visualizations</p>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600 font-medium">Chart Type:</label>
                  <select
                    value={chartType}
                    onChange={(e) => setChartType(e.target.value)}
                    className="input-field w-auto text-sm font-medium"
                  >
                    <option value="bar">Bar Chart</option>
                    <option value="line">Line Chart</option>
                    <option value="pie">Pie Chart</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600 font-medium">Period:</label>
                  <select
                    value={period}
                    onChange={(e) => setPeriod(e.target.value)}
                    className="input-field w-auto text-sm"
                  >
                    <option value="7">Last 7 days</option>
                    <option value="30">Last 30 days</option>
                    <option value="90">Last 90 days</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Revenue Analytics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {/* Revenue - Bar/Line/Pie Chart */}
            <div className="card">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center">
                  <FaChartBar className="mr-2 text-yellow-600" />
                  Revenue Over Time
                </h3>
              </div>
              <div style={{ height: '300px', minHeight: '250px' }}>
                {chartType === 'line' ? (
                  <LineChart 
                    data={analytics?.revenueStats || []} 
                    height="300px"
                    color="#F59E0B"
                    labelKey="_id"
                    valueKey="amount"
                    showArea={true}
                  />
                ) : chartType === 'pie' ? (
                  <PieChart 
                    data={(analytics?.revenueStats || []).slice(-7).map(item => ({
                      label: item._id?.split('-')[2] || item._id,
                      value: item.amount || 0
                    }))}
                    height="300px"
                    colors={['#F59E0B', '#FBBF24', '#FCD34D', '#FDE68A', '#FEF3C7']}
                  />
                ) : (
                  <SimpleChart 
                    data={analytics?.revenueStats || []} 
                    height="300px"
                    color="#F59E0B"
                    labelKey="_id"
                    valueKey="amount"
                  />
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Revenue (Period)</span>
                  <span className="font-bold text-yellow-600">
                    ₹{analytics?.revenueStats?.reduce((sum, item) => sum + (item.amount || 0), 0).toFixed(2) || 0}
                  </span>
                </div>
              </div>
            </div>

            {/* Users Chart */}
            <div className="card">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center">
                  <FaChartLine className="mr-2 text-blue-600" />
                  New Users Over Time
                </h3>
              </div>
              <div style={{ height: '300px', minHeight: '250px' }}>
                {chartType === 'line' ? (
                  <LineChart 
                    data={analytics?.userStats || []} 
                    height="300px"
                    color="#3B82F6"
                    labelKey="_id"
                    valueKey="count"
                    showArea={true}
                  />
                ) : chartType === 'pie' ? (
                  <PieChart 
                    data={(analytics?.userStats || []).slice(-7).map(item => ({
                      label: item._id?.split('-')[2] || item._id,
                      value: item.count || 0
                    }))}
                    height="300px"
                    colors={['#3B82F6', '#60A5FA', '#93C5FD', '#DBEAFE', '#EFF6FF']}
                  />
                ) : (
                  <SimpleChart 
                    data={analytics?.userStats || []} 
                    height="300px"
                    color="#3B82F6"
                    labelKey="_id"
                    valueKey="count"
                  />
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">New Users (Period)</span>
                  <span className="font-bold text-blue-600">
                    {analytics?.userStats?.reduce((sum, item) => sum + (item.count || 0), 0) || 0}
                  </span>
                </div>
              </div>
            </div>

            {/* Trips Chart */}
            <div className="card">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center">
                  <FaChartBar className="mr-2 text-green-600" />
                  Trips Created Over Time
                </h3>
              </div>
              <div style={{ height: '300px', minHeight: '250px' }}>
                {chartType === 'line' ? (
                  <LineChart 
                    data={analytics?.tripStats || []} 
                    height="300px"
                    color="#10B981"
                    labelKey="_id"
                    valueKey="count"
                    showArea={true}
                  />
                ) : chartType === 'pie' ? (
                  <PieChart 
                    data={(analytics?.tripStats || []).slice(-7).map(item => ({
                      label: item._id?.split('-')[2] || item._id,
                      value: item.count || 0
                    }))}
                    height="300px"
                    colors={['#10B981', '#34D399', '#6EE7B7', '#A7F3D0', '#D1FAE5']}
                  />
                ) : (
                  <SimpleChart 
                    data={analytics?.tripStats || []} 
                    height="300px"
                    color="#10B981"
                    labelKey="_id"
                    valueKey="count"
                  />
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">New Trips (Period)</span>
                  <span className="font-bold text-green-600">
                    {analytics?.tripStats?.reduce((sum, item) => sum + (item.count || 0), 0) || 0}
                  </span>
                </div>
              </div>
            </div>

            {/* Bookings Chart */}
            <div className="card">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center">
                  <FaChartLine className="mr-2 text-purple-600" />
                  Bookings Over Time
                </h3>
              </div>
              <div style={{ height: '300px', minHeight: '250px' }}>
                {chartType === 'line' ? (
                  <LineChart 
                    data={analytics?.bookingStats || []} 
                    height="300px"
                    color="#8B5CF6"
                    labelKey="_id"
                    valueKey="count"
                    showArea={true}
                  />
                ) : chartType === 'pie' ? (
                  <PieChart 
                    data={(analytics?.bookingStats || []).slice(-7).map(item => ({
                      label: item._id?.split('-')[2] || item._id,
                      value: item.count || 0
                    }))}
                    height="300px"
                    colors={['#8B5CF6', '#A78BFA', '#C4B5FD', '#DDD6FE', '#EDE9FE']}
                  />
                ) : (
                  <SimpleChart 
                    data={analytics?.bookingStats || []} 
                    height="300px"
                    color="#8B5CF6"
                    labelKey="_id"
                    valueKey="count"
                  />
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">New Bookings (Period)</span>
                  <span className="font-bold text-purple-600">
                    {analytics?.bookingStats?.reduce((sum, item) => sum + (item.count || 0), 0) || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Distribution Charts - Pie Charts */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {/* User Role Distribution */}
            <div className="card">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center">
                  <FaChartPie className="mr-2 text-blue-600" />
                  User Role Distribution
                </h3>
              </div>
              <div style={{ height: '300px', minHeight: '250px' }}>
                <PieChart 
                  data={userRoleData}
                  height="250px"
                  colors={['#3B82F6', '#10B981']}
                />
              </div>
            </div>

            {/* Trip Status Distribution */}
            <div className="card">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center">
                  <FaChartPie className="mr-2 text-green-600" />
                  Trip Status Distribution
                </h3>
              </div>
              <div style={{ height: '300px', minHeight: '250px' }}>
                <PieChart 
                  data={tripStatusData}
                  height="250px"
                  colors={['#10B981', '#3B82F6', '#F59E0B']}
                />
              </div>
            </div>

            {/* Booking Status Distribution */}
            <div className="card">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center">
                  <FaChartPie className="mr-2 text-purple-600" />
                  Booking Status Distribution
                </h3>
              </div>
              <div style={{ height: '300px', minHeight: '250px' }}>
                <PieChart 
                  data={bookingStatusData}
                  height="250px"
                  colors={['#8B5CF6', '#A78BFA']}
                />
              </div>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <div className="card bg-gradient-to-br from-blue-50 to-blue-100">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">Average Daily Revenue</p>
                <p className="text-2xl font-bold text-blue-600">
                  ₹{analytics?.revenueStats?.length > 0 
                    ? (analytics.revenueStats.reduce((sum, item) => sum + (item.amount || 0), 0) / analytics.revenueStats.length).toFixed(2)
                    : 0}
                </p>
              </div>
            </div>
            <div className="card bg-gradient-to-br from-green-50 to-green-100">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">Average Daily Users</p>
                <p className="text-2xl font-bold text-green-600">
                  {analytics?.userStats?.length > 0 
                    ? (analytics.userStats.reduce((sum, item) => sum + (item.count || 0), 0) / analytics.userStats.length).toFixed(1)
                    : 0}
                </p>
              </div>
            </div>
            <div className="card bg-gradient-to-br from-purple-50 to-purple-100">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">Average Daily Trips</p>
                <p className="text-2xl font-bold text-purple-600">
                  {analytics?.tripStats?.length > 0 
                    ? (analytics.tripStats.reduce((sum, item) => sum + (item.count || 0), 0) / analytics.tripStats.length).toFixed(1)
                    : 0}
                </p>
              </div>
            </div>
            <div className="card bg-gradient-to-br from-orange-50 to-orange-100">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">Average Daily Bookings</p>
                <p className="text-2xl font-bold text-orange-600">
                  {analytics?.bookingStats?.length > 0 
                    ? (analytics.bookingStats.reduce((sum, item) => sum + (item.count || 0), 0) / analytics.bookingStats.length).toFixed(1)
                    : 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;

